/**
 * Session / auth-token helpers.
 *
 * The backend issues a JWT that expires after 24h and there is no refresh
 * mechanism. Once it expires, every protected API call returns 401. Previously
 * the app kept showing the user as "logged in" (because the token still sat in
 * localStorage) so those 401s surfaced as misleading "could not connect to
 * server" / failed-to-load errors. These helpers let us detect an expired
 * token and force a clean re-login.
 */

const SESSION_EXPIRED_FLAG = 'sessionExpired';

/** Decode a JWT payload without verifying the signature (client-side only). */
const decodeToken = (token) => {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/**
 * Returns true if there is no token, or the token is malformed, or its `exp`
 * claim is in the past. A small clock-skew allowance is applied.
 */
export const isTokenExpired = (token = localStorage.getItem('token')) => {
  if (!token) return true;
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  const nowSeconds = Date.now() / 1000;
  const skewSeconds = 30; // treat as expired 30s early to avoid edge races
  return decoded.exp <= nowSeconds + skewSeconds;
};

/** Remove all session state from the browser. */
export const clearSession = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  } catch {
    /* ignore */
  }
};

/** Was the last redirect to login caused by an expired/invalid session? */
export const consumeSessionExpiredFlag = () => {
  try {
    const flagged = sessionStorage.getItem(SESSION_EXPIRED_FLAG) === '1';
    if (flagged) sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
    return flagged;
  } catch {
    return false;
  }
};

/**
 * Clear the session and bounce the user back to the login screen with a flag
 * so the login page can explain what happened. Guarded so repeated 401s in a
 * burst only trigger a single redirect.
 */
let handling = false;
export const handleUnauthorized = () => {
  if (handling) return;
  handling = true;
  clearSession();
  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
  } catch {
    /* ignore */
  }
  // Full navigation resets all in-memory React state cleanly.
  if (window.location.pathname !== '/') {
    window.location.assign('/');
  } else {
    window.location.reload();
  }
};
